export const MINIMAP_WIDTH = 60
export const MINIMAP_HEIGHT = 80
export const MINIMAP_ROOM_SIZE = 8
export const MINIMAP_GAP = 1
const MINIMAP_DISPLAY_SCALE = 2

const FRAME_SIZE = 4
const CONTENT_WIDTH = MINIMAP_WIDTH - FRAME_SIZE * 2
const CONTENT_HEIGHT = MINIMAP_HEIGHT - FRAME_SIZE * 2
const ROOM_STEP = MINIMAP_ROOM_SIZE + MINIMAP_GAP

const COLORS = {
  outerEdge: '#09070a',
  innerEdge: '#887d91',
  panel: '#18141b',
  shadow: '#09080a',
  shadowConnection: '#111013',
  hint: '#45404a',
  current: '#f3d37a',
  homeGround: '#6f4a32',
  player: '#d8f6ff',
  locked: '#e7ad3d',
  exit: '#79c9e8',
  homeRoof: '#b44c35',
  homeWall: '#ead7ab',
  homeDoor: '#6f3d25',
  shopRoof: '#8f3e32',
  shopAwning: '#f3d37a',
  shopWall: '#ead7ab',
  shopWindow: '#79c9e8',
  shopDoor: '#6f3d25',
  shopOutline: '#2f1d19'
}

const AREA_COLORS = {
  forest: {
    room: '#4a6a4a',
    connection: '#78915e',
    panel: '#17131a'
  },
  graveyard: {
    room: '#514136',
    connection: '#8a7257',
    panel: '#171313'
  },
  caves: {
    room: '#46566c',
    connection: '#63a9b8'
  },
  emberkeep: {
    room: '#71443b',
    connection: '#d07a4e'
  },
  purpledooms: {
    room: '#563a70',
    connection: '#a365c7'
  }
}

function cellKey(col, row) {
  return `${col},${row}`
}

export function getMinimapLayout(cols, rows) {
  const mapWidth = cols * MINIMAP_ROOM_SIZE + Math.max(0, cols - 1) * MINIMAP_GAP
  const mapHeight = rows * MINIMAP_ROOM_SIZE + Math.max(0, rows - 1) * MINIMAP_GAP

  return {
    mapWidth,
    mapHeight,
    offsetX: FRAME_SIZE + Math.floor((CONTENT_WIDTH - mapWidth) / 2),
    offsetY: FRAME_SIZE + Math.floor((CONTENT_HEIGHT - mapHeight) / 2)
  }
}

export function getMinimapRoomPosition(layout, col, row) {
  return {
    x: layout.offsetX + col * ROOM_STEP,
    y: layout.offsetY + row * ROOM_STEP
  }
}

export class Minimap {
  constructor(game, canvas) {
    this.game = game
    this.canvas = canvas
    this.ctx = canvas?.getContext('2d') || null
    this.button = canvas?.closest?.('#minimap-button') || null
    this.label = this.button?.querySelector?.('#minimap-area-label') || null
    this.lastSignature = ''
    this.expanded = false
    this.expandedWasPaused = false
    this.currentAreaId = null
    this.viewedAreaId = null
    this.description = 'Area map.'

    if (!this.canvas) return
    this.canvas.width = MINIMAP_WIDTH
    this.canvas.height = MINIMAP_HEIGHT
    this.canvas.style.width = `${MINIMAP_WIDTH * MINIMAP_DISPLAY_SCALE}px`
    this.canvas.style.height = `${MINIMAP_HEIGHT * MINIMAP_DISPLAY_SCALE}px`
    this.canvas.hidden = true
    if (this.button) {
      this.button.hidden = true
      this.button.setAttribute('aria-expanded', 'false')
      this.button.addEventListener('click', event => {
        event.preventDefault()
        event.stopPropagation()
        this.setExpanded(!this.expanded)
      })
    }
  }

  update() {
    if (!this.canvas || !this.ctx || !this.game.world || !this.game.player) {
      this.setVisible(false)
      return
    }

    if (this.game.world.isInsideHome?.(this.game.player.x, this.game.player.y)) {
      this.setVisible(false)
      return
    }

    const currentState = this.game.world.getMinimapStateAt(
      this.game.player.x,
      this.game.player.y
    )
    if (!currentState) {
      this.setVisible(false)
      return
    }

    this.currentAreaId = currentState.areaId
    if (!this.expanded || !this.viewedAreaId) {
      this.viewedAreaId = this.currentAreaId
    }
    const state = this.viewedAreaId === this.currentAreaId
      ? currentState
      : this.game.world.getMinimapStateForArea?.(this.viewedAreaId)
    if (!state) {
      this.viewedAreaId = this.currentAreaId
      return this.update()
    }

    this.setLandscape(state.kind === 'village')

    this.setVisible(true)
    this.description = (
      state.kind === 'village'
        ? 'Village map. Houses and fruit shops.'
        : `${state.displayName} map. ${state.exploredCount} of ${state.cells.length} rooms explored. ` +
          (state.mapFound ? 'Area map found.' : 'Area map not found.')
    )
    this.canvas.setAttribute('aria-label', this.description)
    if (this.label) this.label.textContent = state.displayName.toUpperCase()
    this.updateButtonLabel()

    const signature = this.getSignature(state)
    if (signature === this.lastSignature) return
    this.lastSignature = signature
    this.render(state)
  }

  setVisible(visible) {
    if (this.canvas) this.canvas.hidden = !visible
    if (this.button) this.button.hidden = !visible
    if (!visible && this.expanded) this.setExpanded(false)
  }

  setLandscape(landscape) {
    const width = landscape ? MINIMAP_HEIGHT : MINIMAP_WIDTH
    const height = landscape ? MINIMAP_WIDTH : MINIMAP_HEIGHT
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width
      this.canvas.height = height
      this.lastSignature = ''
    }
    this.canvas.style.width = `${width * MINIMAP_DISPLAY_SCALE}px`
    this.canvas.style.height = `${height * MINIMAP_DISPLAY_SCALE}px`
    this.button?.classList.toggle('is-landscape', landscape)
  }

  setExpanded(expanded) {
    const nextExpanded = expanded === true
    if (!this.button || (nextExpanded && this.button.hidden)) return false

    if (nextExpanded !== this.expanded) {
      if (nextExpanded) {
        this.expandedWasPaused = this.game.isPaused === true
        this.game.isPaused = true
        this.game.input?.resetTouchJoystick?.()
      } else {
        this.game.isPaused = this.expandedWasPaused
        this.expandedWasPaused = false
      }
    }

    this.expanded = nextExpanded
    if (!this.expanded) this.viewedAreaId = this.currentAreaId
    this.button.classList.toggle('is-expanded', this.expanded)
    this.button.setAttribute('aria-expanded', String(this.expanded))
    this.updateButtonLabel()
    return true
  }

  browseArea(direction) {
    if (!this.expanded || !Number.isFinite(direction) || direction === 0) return false
    if (this.viewedAreaId === 'village') {
      if (direction < 0 || !this.game.world.getAreaMapState?.('forest')?.mapFound) return false
      this.viewedAreaId = 'forest'
      this.lastSignature = ''
      this.update()
      return true
    }
    const grids = this.game.world?.grids || []
    const currentIndex = grids.findIndex(grid => grid.areaId === this.viewedAreaId)
    const target = grids[currentIndex + Math.sign(direction)]
    if (!target || !this.game.world.getAreaMapState?.(target.areaId)?.mapFound) {
      return false
    }

    this.viewedAreaId = target.areaId
    this.lastSignature = ''
    this.update()
    return true
  }

  browseHorizontal(direction) {
    if (!this.expanded || !Number.isFinite(direction) || direction === 0) return false
    const targetAreaId = direction < 0 && this.viewedAreaId === 'village'
      ? 'forest'
      : direction > 0 && this.viewedAreaId === 'forest' &&
          this.game.world?.postgameVillage?.unlocked
        ? 'village'
        : null
    if (!targetAreaId) return false
    if (targetAreaId === 'forest' && !this.game.world.getAreaMapState?.('forest')?.mapFound) {
      return false
    }
    this.viewedAreaId = targetAreaId
    this.lastSignature = ''
    this.update()
    return true
  }

  updateButtonLabel() {
    this.button?.setAttribute(
      'aria-label',
      `${this.description} ${this.expanded ? 'Close enlarged map.' : 'Enlarge map.'}`
    )
  }

  getSignature(state) {
    const explored = state.cells
      .filter(cell => cell.explored)
      .map(cell => cellKey(cell.col, cell.row))
      .sort()
      .join('|')
    const doors = state.connections
      .filter(connection => connection.locked)
      .map(connection => `${connection.col},${connection.row}:${connection.direction}`)
      .sort()
      .join('|')

    return [
      state.areaId,
      state.mapFound ? 1 : 0,
      explored,
      doors,
      state.currentCell.col,
      state.currentCell.row,
      state.homeCell ? `${state.homeCell.col},${state.homeCell.row}` : '',
      state.shopCell ? `${state.shopCell.col},${state.shopCell.row}` : ''
    ].join(';')
  }

  render(state) {
    if (state.kind === 'village') {
      this.renderVillage(state)
      return
    }
    const ctx = this.ctx
    const layout = getMinimapLayout(state.cols, state.rows)
    const areaColors = AREA_COLORS[state.areaId] || AREA_COLORS.graveyard
    const explored = new Set(
      state.cells
        .filter(cell => cell.explored)
        .map(cell => cellKey(cell.col, cell.row))
    )
    const hinted = new Set()

    ctx.clearRect(0, 0, MINIMAP_WIDTH, MINIMAP_HEIGHT)
    ctx.fillStyle = COLORS.outerEdge
    ctx.fillRect(0, 0, MINIMAP_WIDTH, MINIMAP_HEIGHT)
    ctx.fillStyle = COLORS.innerEdge
    ctx.fillRect(2, 2, MINIMAP_WIDTH - 4, MINIMAP_HEIGHT - 4)
    ctx.fillStyle = areaColors.panel || COLORS.panel
    ctx.fillRect(
      FRAME_SIZE,
      FRAME_SIZE,
      MINIMAP_WIDTH - FRAME_SIZE * 2,
      MINIMAP_HEIGHT - FRAME_SIZE * 2
    )
    if (!state.mapFound) {
      for (const connection of state.connections) {
        const fromKey = cellKey(connection.col, connection.row)
        const toKey = connection.toCol === null
          ? null
          : cellKey(connection.toCol, connection.toRow)
        if (explored.has(fromKey) && toKey && !explored.has(toKey)) hinted.add(toKey)
        if (toKey && explored.has(toKey) && !explored.has(fromKey)) hinted.add(fromKey)
      }
    }

    for (const connection of state.connections) {
      const fromKey = cellKey(connection.col, connection.row)
      const toKey = connection.toCol === null
        ? null
        : cellKey(connection.toCol, connection.toRow)
      const fromExplored = explored.has(fromKey)
      const toExplored = toKey ? explored.has(toKey) : false

      if (!state.mapFound && !fromExplored && !toExplored) continue

      let color = state.mapFound ? COLORS.shadowConnection : COLORS.hint
      if (fromExplored && (toExplored || connection.exit)) {
        color = connection.exit ? COLORS.exit : areaColors.connection
      }
      if (connection.locked && (fromExplored || toExplored)) {
        color = COLORS.locked
      }
      this.drawConnection(layout, connection, color)
    }

    for (const cell of state.cells) {
      const key = cellKey(cell.col, cell.row)
      const room = getMinimapRoomPosition(layout, cell.col, cell.row)

      if (cell.explored) {
        ctx.fillStyle = areaColors.room
        ctx.fillRect(room.x, room.y, MINIMAP_ROOM_SIZE, MINIMAP_ROOM_SIZE)
        this.drawAreaRoomTexture(state.areaId, room)
      } else if (state.mapFound) {
        ctx.fillStyle = COLORS.shadow
        ctx.fillRect(room.x, room.y, MINIMAP_ROOM_SIZE, MINIMAP_ROOM_SIZE)
      } else if (hinted.has(key)) {
        ctx.fillStyle = COLORS.shadow
        ctx.fillRect(room.x, room.y, MINIMAP_ROOM_SIZE, MINIMAP_ROOM_SIZE)
      }
    }

    const currentRoom = getMinimapRoomPosition(
      layout,
      state.currentCell.col,
      state.currentCell.row
    )
    const currentIsHome = state.homeCell &&
      state.homeCell.col === state.currentCell.col &&
      state.homeCell.row === state.currentCell.row
    if (!state.browsing) {
      ctx.fillStyle = currentIsHome ? COLORS.homeGround : COLORS.current
      ctx.fillRect(currentRoom.x, currentRoom.y, MINIMAP_ROOM_SIZE, MINIMAP_ROOM_SIZE)
      this.drawAreaRoomTexture(state.areaId, currentRoom)
    }

    if (state.homeCell) {
      this.drawHomeMarker(layout, state.homeCell)
    }
    if (state.shopCell) {
      this.drawShopMarker(layout, state.shopCell)
    }

    if (state.browsing) return
    ctx.fillStyle = COLORS.player
    const playerIsAtHome = currentIsHome
    const playerIsAtShop = state.shopCell &&
      state.shopCell.col === state.currentCell.col &&
      state.shopCell.row === state.currentCell.row
    const playerX = playerIsAtShop ? 5 : playerIsAtHome ? 4 : 2
    const playerY = playerIsAtShop ? 0 : playerIsAtHome ? 4 : 2
    const playerSize = playerIsAtHome || playerIsAtShop ? 1 : 2
    ctx.fillRect(
      currentRoom.x + playerX,
      currentRoom.y + playerY,
      playerSize,
      playerSize
    )
  }

  renderVillage(state) {
    const ctx = this.ctx
    const canvasWidth = this.canvas.width
    const canvasHeight = this.canvas.height
    const contentWidth = canvasWidth - FRAME_SIZE * 2
    const contentHeight = canvasHeight - FRAME_SIZE * 2
    const left = FRAME_SIZE + 3
    const top = FRAME_SIZE + 7
    const width = contentWidth - 6
    const height = contentHeight - 14
    const project = point => ({
      x: left + Math.round((point.x - state.bounds.minX) /
        Math.max(1, state.bounds.maxX - state.bounds.minX) * (width - 1)),
      y: top + Math.round((point.y - state.bounds.minY) /
        Math.max(1, state.bounds.maxY - state.bounds.minY) * (height - 1))
    })

    ctx.clearRect(0, 0, canvasWidth, canvasHeight)
    ctx.fillStyle = COLORS.outerEdge
    ctx.fillRect(0, 0, canvasWidth, canvasHeight)
    ctx.fillStyle = COLORS.innerEdge
    ctx.fillRect(2, 2, canvasWidth - 4, canvasHeight - 4)
    ctx.fillStyle = COLORS.panel
    ctx.fillRect(FRAME_SIZE, FRAME_SIZE, contentWidth, contentHeight)

    // One continuous village lawn, not dungeon rooms or doorways.
    ctx.fillStyle = '#355d38'
    ctx.fillRect(left, top, width, height)
    ctx.fillStyle = '#4f7b45'
    ctx.fillRect(left + 1, top + 1, width - 2, height - 2)

    // Side-to-side paths connect every home and fruit shop.
    ctx.fillStyle = '#c7a46b'
    const centerY = top + Math.floor(height / 2)
    ctx.fillRect(left + 2, centerY - 1, width - 4, 3)
    for (const building of [...state.houses, ...state.shops]) {
      const point = project(building)
      const pathTop = Math.min(centerY, point.y)
      ctx.fillRect(point.x, pathTop, 2, Math.max(2, Math.abs(point.y - centerY) + 1))
    }

    for (const house of state.houses) {
      const point = project(house)
      ctx.fillStyle = house.playerHome ? '#d85b45' : COLORS.homeRoof
      ctx.fillRect(point.x - 2, point.y - 2, 5, 2)
      ctx.fillStyle = house.playerHome ? '#f0c989' : COLORS.homeWall
      ctx.fillRect(point.x - 2, point.y, 5, 3)
      ctx.fillStyle = COLORS.homeDoor
      ctx.fillRect(point.x, point.y + 1, 1, 2)
      if (house.playerHome) {
        ctx.fillStyle = '#ffe27a'
        ctx.fillRect(point.x - 1, point.y - 3, 3, 1)
      }
    }
    for (const shop of state.shops) {
      const point = project(shop)
      ctx.fillStyle = COLORS.shopOutline
      ctx.fillRect(point.x - 2, point.y - 2, 5, 5)
      ctx.fillStyle = COLORS.shopRoof
      ctx.fillRect(point.x - 2, point.y - 2, 5, 2)
      ctx.fillStyle = COLORS.shopAwning
      ctx.fillRect(point.x - 1, point.y - 1, 1, 1)
      ctx.fillRect(point.x + 1, point.y - 1, 1, 1)
      ctx.fillStyle = COLORS.shopWall
      ctx.fillRect(point.x - 1, point.y, 3, 2)
    }

    if (!state.browsing) {
      const player = project(state.player)
      ctx.fillStyle = COLORS.player
      ctx.fillRect(player.x, player.y, 2, 2)
    }
  }

  drawAreaRoomTexture(areaId, room) {
    // Area rooms intentionally use their solid map color without decoration.
  }

  drawHomeMarker(layout, homeCell) {
    const room = getMinimapRoomPosition(layout, homeCell.col, homeCell.row)
    this.ctx.fillStyle = COLORS.homeRoof
    this.ctx.fillRect(room.x + 2, room.y + 1, 2, 1)
    this.ctx.fillRect(room.x + 1, room.y + 2, 4, 1)
    this.ctx.fillStyle = COLORS.homeWall
    this.ctx.fillRect(room.x + 1, room.y + 3, 4, 2)
    this.ctx.fillStyle = COLORS.homeDoor
    this.ctx.fillRect(room.x + 3, room.y + 4, 1, 1)
  }

  drawShopMarker(layout, shopCell) {
    const room = getMinimapRoomPosition(layout, shopCell.col, shopCell.row)
    // Full-room storefront silhouette so it remains recognizable on the
    // compact map as well as the enlarged map.
    this.ctx.fillStyle = COLORS.shopOutline
    this.ctx.fillRect(room.x + 2, room.y, 2, 1)
    this.ctx.fillRect(room.x + 1, room.y + 1, 4, 1)
    this.ctx.fillRect(room.x, room.y + 2, 6, 4)

    this.ctx.fillStyle = COLORS.shopRoof
    this.ctx.fillRect(room.x + 2, room.y, 2, 1)
    this.ctx.fillRect(room.x + 1, room.y + 1, 4, 1)
    this.ctx.fillRect(room.x, room.y + 2, 1, 1)
    this.ctx.fillRect(room.x + 2, room.y + 2, 1, 1)
    this.ctx.fillRect(room.x + 4, room.y + 2, 1, 1)

    this.ctx.fillStyle = COLORS.shopAwning
    this.ctx.fillRect(room.x + 1, room.y + 2, 1, 1)
    this.ctx.fillRect(room.x + 3, room.y + 2, 1, 1)
    this.ctx.fillRect(room.x + 5, room.y + 2, 1, 1)

    this.ctx.fillStyle = COLORS.shopWall
    this.ctx.fillRect(room.x, room.y + 3, 6, 3)

    this.ctx.fillStyle = COLORS.shopWindow
    this.ctx.fillRect(room.x + 1, room.y + 4, 2, 1)
    this.ctx.fillStyle = COLORS.shopOutline
    this.ctx.fillRect(room.x + 2, room.y + 4, 1, 1)

    this.ctx.fillStyle = COLORS.shopDoor
    this.ctx.fillRect(room.x + 4, room.y + 4, 1, 2)
    this.ctx.fillStyle = COLORS.shopOutline
    this.ctx.fillRect(room.x, room.y + 5, 6, 1)
    this.ctx.fillStyle = COLORS.shopDoor
    this.ctx.fillRect(room.x + 4, room.y + 5, 1, 1)
  }

  drawConnection(layout, connection, color) {
    const ctx = this.ctx
    const room = getMinimapRoomPosition(layout, connection.col, connection.row)
    const positions = {
      n: { x: room.x + 2, y: room.y - 2 },
      e: { x: room.x + MINIMAP_ROOM_SIZE, y: room.y + 2 },
      s: { x: room.x + 2, y: room.y + MINIMAP_ROOM_SIZE },
      w: { x: room.x - 2, y: room.y + 2 }
    }
    const point = positions[connection.direction]
    if (!point) return

    ctx.fillStyle = color
    ctx.fillRect(point.x, point.y, 2, 2)
  }
}
